import { motion } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { fadeInUp, staggerContainer, fadeIn } from "@/lib/motion";
import { PageHeader } from "@/components/layout/PageHeader";
import { useLanguage } from "@/components/LanguageContext";

export default function Partners() {
  const { t } = useLanguage();

  const groups = [
    {
      category: t("Autorités Publiques & Ministères", "Public Authorities & Ministries"),
      description: t(
        "Nous accompagnons les décideurs publics dans la définition, la modélisation et l'évaluation de leurs politiques de mobilité. De la restructuration des réseaux de transport en commun en site propre (TCSP) à l'aménagement de pôles d'échanges multimodaux, AVISIUM fournit les diagnostics territoriaux et les jumeaux numériques nécessaires pour garantir des investissements optimisés.",
        "We support public decision-makers in defining, modeling, and evaluating their mobility policies. From restructuring Bus Rapid Transit (BRT) networks to designing multimodal interchange hubs, AVISIUM provides the territorial diagnostics and digital twins necessary to guarantee optimized investments."
      ),
      logos: [
        { src: "/images/clients/ministere-transport.jpg", alt: "Ministère de l'Équipement et de l'Eau" },
        { src: "/images/clients/autoroute-du-maroc.png", alt: "Autoroutes du Maroc" },
        { src: "/images/clients/ville-marrakech.png", alt: "Ville de Marrakech" },
        { src: "/images/clients/casa-transport.png", alt: "Casa Transport" },
        { src: "/images/clients/agadir-mobilite.png", alt: "Agadir Mobilité" }
      ]
    },
    {
      category: t("Organisations Internationales & Bailleurs", "International Organizations & Funding Agencies"),
      description: t(
        "AVISIUM intervient en tant qu'expert technique et stratégique auprès des bailleurs de fonds internationaux pour concevoir des programmes de mobilité durable. Nos missions incluent la structuration de zones à faibles émissions (ZFE), l'intégration de flottes électriques et l'élaboration de feuilles de route pour la décarbonation urbaine.",
        "AVISIUM acts as a technical and strategic expert for international donors to design sustainable mobility programs. Our missions include the structuring of Low-Emission Zones (ZFE), the integration of electric fleets, and the development of roadmaps for urban decarbonization."
      ),
      logos: [
        { src: "/images/clients/world-bank.png", alt: "World Bank Group" },
        { src: "/images/clients/pnud.png", alt: "UNDP" },
        { src: "/images/clients/gef.png", alt: "Global Environment Facility" }
      ]
    },
    {
      category: t("Ingénierie, Industrie & Innovation", "Engineering, Industry & Innovation"),
      description: t(
        "En partenariat avec les leaders de l'ingénierie civile, les constructeurs de transport et les institutions académiques, nous apportons notre expertise de niche en micro-simulation dynamique (Vissim), en ingénierie de trafic et en intelligence artificielle appliquée pour relever les défis complexes d'infrastructure.",
        "In partnership with civil engineering leaders, transit vehicle manufacturers, and academic institutions, we provide niche expertise in dynamic micro-simulation (Vissim), traffic engineering, and applied artificial intelligence to solve complex infrastructure challenges."
      ),
      logos: [
        { src: "/images/clients/cid.jpg", alt: "CID" },
        { src: "/images/clients/artelia.png", alt: "Artelia" },
        { src: "/images/clients/novec.jpg", alt: "Novec" },
        { src: "/images/clients/jesa.png", alt: "JESA" },
        { src: "/images/clients/Glydways.jpg", alt: "Glydways" },
        { src: "/images/clients/um6p.png", alt: "UM6P" },
        { src: "/images/clients/ehtp.png", alt: "EHTP" }
      ]
    }
  ];

  return (
    <>
      <PageHeader 
        overlineFr="Réseau & Confiance"
        overlineEn="Network & Trust"
        titleFr="La Confiance des Leaders."
        titleEn="Trusted by Leaders."
        descriptionFr="Nous collaborons avec des agences publiques, des banques internationales et de grands groupes d'ingénierie pour fournir un impact à grande échelle."
        descriptionEn="We collaborate with public agencies, international development banks, and major engineering groups to deliver impact at scale."
      />

      <section className="py-20 lg:py-24 bg-mineral/50">
        <Container>
           <div className="space-y-24">
             {groups.map((group, i) => (
                 <div key={i} className="mb-24 last:mb-0">
                   <div className="max-w-4xl mx-auto text-center mb-12">
                     <motion.h3 
                       variants={fadeInUp} 
                       initial="hidden" 
                       whileInView="visible" 
                       viewport={{ once: true }}
                       className="text-2xl font-display text-charcoal font-semibold mb-4"
                     >
                       {group.category}
                     </motion.h3>
                     <motion.p
                       variants={fadeInUp}
                       initial="hidden"
                       whileInView="visible"
                       viewport={{ once: true }}
                       className="text-graphite leading-relaxed text-sm md:text-base"
                     >
                       {group.description}
                     </motion.p>
                   </div>
                   
                   <motion.div 
                     variants={staggerContainer}
                     initial="hidden"
                     whileInView="visible"
                     viewport={{ once: true, margin: "-50px" }}
                     className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6"
                   >
                      {group.logos.map((logo, j) => (
                        <motion.div 
                          key={j} 
                          variants={fadeIn}
                          className="flex items-center justify-center p-4 h-40 rounded-2xl bg-warm-white border border-mineral-dark hover:border-amber/30 hover:shadow-lg hover:shadow-amber/5 transition-all duration-500 overflow-hidden relative"
                        >
                           <img 
                             src={logo.src} 
                             alt={logo.alt} 
                             className="w-full h-full object-contain p-2 mix-blend-multiply transition-all" 
                           />
                        </motion.div>
                      ))}
                   </motion.div>
                 </div>
             ))}
           </div>
        </Container>
      </section>
    </>
  );
}
